import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import PlaceholderImage from "../../assets/images/social-placeholder.png";

const SEO = ({
  description,
  lang,
  meta,
  keywords,
  title,
  socialImage = PlaceholderImage,
  uri = "",
  noindex,
  breadcrumbs = []
}) => {
  const imageMeta = (siteUrl) =>
    siteUrl && socialImage
      ? [
          {
            property: "og:image",
            content: siteUrl + socialImage,
          },
          {
            property: "og:image:width",
            content: 500,
          },
          {
            property: "og:image:height",
            content: 500,
          },
          {
            name: "twitter:image",
            content: siteUrl + socialImage,
          },
          {
            name: "twitter:image:width",
            content: 500,
          },
          {
            name: "twitter:image:height",
            content: 500,
          },
          {
            name: "twitter:image:alt",
            content: process.env.SITE_NAME,
          },
        ]
      : [];
  const siteUrl = process.env.SITE_URL || "";
  const metaDescription = description || 'See more info on ' + process.env.SITE_URL;
  const titleText = title ? `${title}` : 'Welcome to ' + process.env.SITE_NAME;
  const breadcrumbsArray = [];
  if (breadcrumbs.length) {
    let breadcrumbPosition = 1;
    breadcrumbs.forEach(function(element) {
      let url = element.url ? siteUrl + element.url : siteUrl + "/";
      let breadcrumbRepresentation = {
        "@type": "ListItem",
        "position": breadcrumbPosition,
        "name": element.text,
        "item": url,
      };
      breadcrumbsArray.push(JSON.stringify(breadcrumbRepresentation));
      breadcrumbPosition++;
    });
  }
  const canonicalUri = uri.endsWith("/") ? siteUrl + uri : siteUrl + uri + "/";

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={titleText}
      titleTemplate={`%s`}
      link={[
        {
          rel: "canonical",
          href: canonicalUri,
        },
        {
          rel: "image_src",
          href: siteUrl + uri,
        },
        {
          rel: "preload",
          as: "font",
          crossorigin: "anonymous",
          href: "/fonts/kanit/kanit-v7-thai_latin-500.woff2",
        },
        {
          rel: "preload",
          as: "font",
          crossorigin: "anonymous",
          href: "/fonts/kanit/kanit-v7-thai_latin-regular.woff2",
        }
      ]}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: `og:locale`,
          content: `th_TH`,
        },
        {
          property: "og:url",
          content: siteUrl + uri,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:site`,
          content: process.env.TWITTER_CREATOR,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `google-site-verification`,
          content: process.env.GOOGLE_SITE_VERIFICATION,
        },
        {
          name: `robots`,
          content: `max-image-preview:standard`,
        },
        {
          name: `thumbnail`,
          content: siteUrl + socialImage,
        }
      ]
        .concat(
          keywords.length > 0
            ? {
              name: `keywords`,
              content: keywords.join(`, `),
            }
            : []
        )
        .concat(
          noindex
            ? {
              name: `robots`,
              content: `noindex`,
            } : []
        )
        .concat(imageMeta(siteUrl))
        .concat(meta)}
    >
      {title &&
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "mainEntityOfPage": {
              "@type": "WebPage",
               "@id": "https://google.com/article"
            },
            "headline": "${title}",
            "description": "${description}",
            "image": ["${siteUrl + socialImage}"],
            "publisher": {
              "@type": "Organization",
              "name": "${process.env.SEO_ORG_NAME}",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.gclub-casino.com/logo.png"
              }
            },
            "author": {
              "@type": "Person",
              "name": "บทความ",
              "url": "${siteUrl + "/blog/"}"
            }
          }
        `}
      </script>
      }
      {breadcrumbsArray.length &&
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [${breadcrumbsArray}]
            }
            `
          }
        </script>
      }
    </Helmet>
  );
}

SEO.defaultProps = {
  lang: `th-TH`,
  meta: [],
  keywords: [],
  noindex: false,
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.array,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
  socialImage: PropTypes.string,
  noindex: PropTypes.bool,
  breadcrumbs: PropTypes.array,
};

export default SEO;
